import {
  useMemo,
  useEffect,
  useRef,
  useState,
  type CSSProperties
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import styles from './styles.module.css';
import {
  transformHistoryData,
  getDateRangeByInterval,
  getApiInterval,
  intervalOptions,
  type IntervalType,
  getTurbines,
  getNewHistoryDataBySocket,
  getNewGenTotalHistoryDataBySocket,
  setDataForGaugesBySocket,
  formatGenTotalValue
} from './utils';
import { getGaugeGridColumnCount } from './utils/getPanelResizeBounds';
import { MAX_RESIZED_GAUGE_GRID_COLUMNS } from './utils/panelResizeConstants';
import { useHorizontalPanelResize } from './utils/useHorizontalPanelResize';

const VIEW_REPORTING = 'reporting';
const POWER_CHART_RIGHT_MARGIN = 80;
const VIEW_GEN_TOTAL = 'genTotal';
const VIEW_ALL_PLUS = 'allPlus';
import { GaugeData } from '../GlobalViewContainer/types';
import LiveIcon from '@/assets/icons/live.svg?react';
import PanelResizeIcon from '@/assets/icons/panel-resize.svg?react';
import RefreshIcon from '@/assets/icons/refresh.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import ChangeView from '@/components/ChangeView';
import EmptyState from '@/components/EmptyState';
import Select from '@/components/Form/Select';
import Gauge from '@/components/Gauge';
import GridContainer from '@/components/GridContainer';
import PowerChart from '@/components/PowerChart';
import { toGenTotalMwChartData } from '@/components/PowerChart/utils';
import { socketsEntities } from '@/constants/navigationPaths';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useGetInstalledProducts } from '@/hooks/services/Locations';
import { useGetTurbinesHistorySeries } from '@/hooks/services/Turbines';
import { useLocationGenTotalSocket } from '@/hooks/useLocationGenTotalSocket';
import { saveLocationName } from '@/slices/installedProductsSlice';
import delay from '@/solaris-lens-common-lib/src/utils';
import { processInstalledProducts } from '@/solaris-lens-common-lib/src/utils/turbines';
import { getHistoryUnitPath } from '@/solaris-lens-common-lib/src/utils/turbinesPaths';

export default function LocationViewContainer() {
  const navigate = useNavigate();
  const { setCustomBreadcrumbArray } = useNavigationBar();
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams] = useSearchParams();
  const { setEntityData, socketData, readyState } = useWebSocket();
  const { genTotalMWFromSocket, genTotalSocketUpdate } =
    useLocationGenTotalSocket(siteId || '');
  const [installedProductsState, setInstalledProductsState] = useState<
    GaugeData[]
  >([]);
  const [genTotalMW, setGenTotalMW] = useState<number | null>(null);
  const dispatch = useDispatch();
  const { showToast } = useToast();

  useEffect(() => {
    const locationName = searchParams.get('name');
    if (locationName) {
      setCustomBreadcrumbArray({
        label: locationName,
        path: `/locations/${siteId}?name=${locationName}`
      });
      dispatch(saveLocationName({ locationName }));
    }

    return () => {
      setCustomBreadcrumbArray({});
    };
  }, [searchParams, dispatch, siteId, setCustomBreadcrumbArray]);

  const { data, isLoading, error } = useGetInstalledProducts(siteId || '', {
    useQueryOptions: {
      enabled: Boolean(siteId)
    }
  });

  useEffect(() => {
    if (data?.data?.power) {
      setInstalledProductsState(processInstalledProducts(data.data.power));
    }
    if (data?.data?.genTotalMW != null) {
      setGenTotalMW(data.data.genTotalMW);
    }
  }, [data]);

  useEffect(() => {
    if (socketData?.power) {
      setInstalledProductsState(processInstalledProducts(socketData.power));
    }
  }, [socketData]);

  useEffect(() => {
    if (genTotalMWFromSocket != null) {
      setGenTotalMW(genTotalMWFromSocket);
    }
  }, [genTotalMWFromSocket]);

  useEffect(() => {
    if (isLoading || installedProductsState.length === 0) return;

    delay(1000).then(() => {
      const turbineIds = getTurbines(installedProductsState);

      if (turbineIds.length > 0) {
        setEntityData({
          entityName: socketsEntities.pulseSiteSummary,
          entityId: turbineIds.join(',')
        });
      }
    });
  }, [isLoading, installedProductsState.length]);

  useEffect(() => {
    return () => {
      setEntityData({ entityName: '', entityId: '' });
    };
  }, [location.pathname]);

  const installedProducts = useMemo(() => {
    return installedProductsState;
  }, [installedProductsState]);

  const turbineIds = useMemo(() => {
    return installedProducts.map(product => product.id).join(',');
  }, [installedProducts]);

  const [viewMode, setViewMode] = useState(VIEW_ALL_PLUS);
  const [interval, setInterval] = useState<IntervalType>('live');
  const [isLive, setIsLive] = useState(true);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const dateRange = useMemo(
    () => getDateRangeByInterval(interval),
    [interval, historyRefreshKey]
  );

  const apiInterval = useMemo(() => getApiInterval(interval), [interval]);

  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError
  } = useGetTurbinesHistorySeries(
    {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      category: 'measures',
      metric: 'genTotalKwAnalog',
      scope: 'generator',
      interval: apiInterval,
      ids: turbineIds
    },
    {
      useQueryOptions: {
        enabled: Boolean(turbineIds)
      }
    }
  );

  const [historyDataState, setHistoryDataState] = useState<any[]>([]);

  useEffect(() => {
    if (readyState !== 1) return;
    if (installedProducts?.length !== 0 && socketData?.power?.[0]) {
      const newInstalledProductsState = setDataForGaugesBySocket(
        installedProducts,
        socketData
      );
      if (!newInstalledProductsState) return;
      setInstalledProductsState(newInstalledProductsState);

      if (!isLive || viewMode === VIEW_GEN_TOTAL) return;

      setHistoryDataState(prevHistoryData => {
        if (!prevHistoryData || !prevHistoryData?.length)
          return prevHistoryData;

        const newHistoryData = getNewHistoryDataBySocket(
          prevHistoryData,
          socketData
        );
        return newHistoryData;
      });
    }
  }, [socketData, installedProducts?.length, readyState, isLive, viewMode]);

  useEffect(() => {
    if (!isLive || viewMode !== VIEW_GEN_TOTAL) return;
    if (!genTotalSocketUpdate) return;

    setHistoryDataState(prevHistoryData => {
      if (!prevHistoryData?.length) return prevHistoryData;

      return getNewGenTotalHistoryDataBySocket(
        prevHistoryData,
        genTotalSocketUpdate.genTotalMW,
        genTotalSocketUpdate.sentAt
      );
    });
  }, [genTotalSocketUpdate, isLive, viewMode]);

  useEffect(() => {
    if (historyData?.data?.series?.length) {
      setHistoryDataState(transformHistoryData(historyData.data));
    }
  }, [historyData]);

  const chartData = useMemo(() => {
    if (!historyDataState?.length) return [];
    if (viewMode === VIEW_GEN_TOTAL) {
      return toGenTotalMwChartData(historyDataState);
    }
    return historyDataState;
  }, [historyDataState, viewMode]);

  useEffect(() => {
    if (error) {
      showToast('Unable to load installed products. Please try again.', {
        type: 'error',
        autoClose: 5000
      });
    }
  }, [error, showToast]);

  useEffect(() => {
    if (historyError) {
      showToast('Unable to load historical data.', {
        type: 'error',
        autoClose: 5000
      });
    }
  }, [historyError, showToast]);

  const handleRefresh = () => {
    setHistoryRefreshKey(key => key + 1);
  };

  const columnCount = Math.min(Math.max(installedProducts.length, 1), 5);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const canPersistPanelWidth = !isLoading && installedProducts.length > 0;
  const { leftPanelWidth, isResizing, isResizeEnabled, handlePointerDown } =
    useHorizontalPanelResize({
      containerRef: contentContainerRef,
      leftPanelRef,
      canPersistWidth: canPersistPanelWidth
    });
  const unit = getHistoryUnitPath(historyData);
  const isLeftPanelResized = leftPanelWidth != null;
  const resizedGridColumnCount =
    isLeftPanelResized && leftPanelWidth != null
      ? getGaugeGridColumnCount(leftPanelWidth, MAX_RESIZED_GAUGE_GRID_COLUMNS)
      : null;

  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <ContainerLoader />
      </div>
    );
  }

  const isGenTotalView = viewMode === VIEW_GEN_TOTAL;
  const chartUnit = isGenTotalView ? 'MW' : unit;
  return (
    <div className={styles.mainContainer}>
      {!installedProducts.length ? (
        <EmptyState
          title="No installed products"
          subtitle="No installed products found for this location."
          icon={true}
        />
      ) : (
        <div className={styles.contentContainer} ref={contentContainerRef}>
          <div
            ref={leftPanelRef}
            className={`${styles.leftPanel} ${styles[`column${columnCount}`]} ${
              isLeftPanelResized ? styles.leftPanelResized : ''
            }`}
            style={
              isLeftPanelResized && leftPanelWidth != null
                ? ({
                    width: leftPanelWidth,
                    '--gauge-grid-column-count': resizedGridColumnCount
                  } as CSSProperties)
                : undefined
            }
          >
            <GridContainer
              className={styles.gaugesGrid}
              minItemWidth="215px"
              gap="20px"
              align="left"
              gridTemplateColumns={
                resizedGridColumnCount != null
                  ? `repeat(${resizedGridColumnCount}, 215px)`
                  : undefined
              }
            >
              {installedProducts.map(product => (
                <Gauge
                  key={product.id}
                  title={product.title}
                  subtitle={product.subtitle}
                  mainLabel={product.mainLabel}
                  currentKw={product.currentKw}
                  maxKw={product.maxKw}
                  metrics={product.metrics}
                  variant={product.variant}
                  powerStatus={product.powerStatus}
                  onClick={() => {
                    const locationName = searchParams.get('name');
                    navigate(
                      `/turbines/${product.id}?name=${encodeURIComponent(product.title)}`,
                      {
                        state: {
                          fromLocation: {
                            siteId: siteId || '',
                            locationName: locationName || ''
                          }
                        }
                      }
                    );
                  }}
                />
              ))}
            </GridContainer>
          </div>

          {isResizeEnabled && (
            <div
              id="locationViewPanelResizeHandle"
              className={`${styles.resizeHandle} ${
                isResizing ? styles.resizeHandleActive : ''
              }`}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panels"
              onPointerDown={handlePointerDown}
            >
              <div className={styles.resizeHandleIcon}>
                <PanelResizeIcon />
              </div>
            </div>
          )}

          <div className={styles.rightPanel}>
            <div className={styles.chartHeader}>
              <div className={styles.genTotalContainer}>
                <span className={styles.genTotalLabelText}>Gen Total</span>
                <div className={styles.genTotalValue}>
                  <span className={styles.genTotalValueText}>
                    {genTotalMW != null
                      ? formatGenTotalValue(genTotalMW)
                      : '--'}
                  </span>
                  <span className={styles.genTotalValueUnit}>MW</span>
                </div>
              </div>
              <div className={styles.headerControls}>
                <div className={styles.viewToggle}>
                  <span className={styles.viewLabel}>VIEW</span>
                  <ChangeView
                    selected={viewMode}
                    onClickView={setViewMode}
                    leftValue={VIEW_GEN_TOTAL}
                    rightValue={VIEW_ALL_PLUS}
                    leftContent={
                      <span className={styles.viewToggleText}>Gen Total</span>
                    }
                    rightContent={
                      <span className={styles.viewToggleText}>All</span>
                    }
                  />
                </div>
                <div className={styles.intervalGroup}>
                  <Select
                    label="INTERVAL"
                    value={interval}
                    onChange={value => {
                      setInterval(value as IntervalType);
                      setIsLive(value === intervalOptions[0].value);
                    }}
                    options={intervalOptions}
                    customClass={styles.intervalSelect}
                  />
                  <div className={styles.intervalActions}>
                    {isLive && (
                      <div className={styles.liveIcon} title="Live mode">
                        <LiveIcon />
                      </div>
                    )}

                    {!isLive && (
                      <button
                        className={styles.refreshButton}
                        onClick={handleRefresh}
                        title="Refresh"
                      >
                        <RefreshIcon />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <PowerChart
              data={chartData}
              unit={chartUnit}
              isLoading={isLoadingHistory}
              rightMargin={POWER_CHART_RIGHT_MARGIN}
              hideZeroLines={!isGenTotalView}
              showAggregateLine={isGenTotalView}
              showTurbineLines={!isGenTotalView}
              animateSocketValue={
                socketData?.power?.length > 0 ? socketData?.power?.[0]?.id : ''
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
